import { Component, OnDestroy, OnInit } from '@angular/core';
import { SciChartSurface } from 'scichart/Charting/Visuals/SciChartSurface';
import { NumericAxis } from 'scichart/Charting/Visuals/Axis/NumericAxis';
import { XyDataSeries } from 'scichart/Charting/Model/XyDataSeries';
import { FastLineRenderableSeries } from 'scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries';
import { TemperatureDataRow } from '../models/temperature-data-row';
import { ProjectService } from '../services/project.service';
import { ChartCaptureService } from '../services/chart-capture.service';
import { ENumericFormat } from 'scichart/types/NumericFormat';
import { EAutoRange } from 'scichart/types/AutoRange';
import { EAxisAlignment } from 'scichart/types/AxisAlignment';
import { NumberRange } from 'scichart/Core/NumberRange';
import { ZoneDataRow } from '../models/zone-data-row';
import {
  ELabelPlacement,
  ELegendOrientation,
  FastMountainRenderableSeries,
  GradientParams,
  HeatmapColorMap,
  HorizontalLineAnnotation,
  LegendModifier,
  Point,
  RolloverModifier,
  RubberBandXyZoomModifier,
  TSciChart,
  UniformHeatmapDataSeries,
  UniformHeatmapRenderableSeries,
  VerticalLineAnnotation,
  ZoomPanModifier,
  zeroArray2D,
} from 'scichart';
import { Subscription, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ProjectTemperatureData } from '../models/project-temperature-data';
import { MarkerInfo } from '../models/marker-info';

@Component({
  selector: 'app-curing',
  templateUrl: './curing.component.html',
  styleUrls: ['./curing.component.scss'],
})
export class CuringComponent implements OnInit, OnDestroy {
  errorMessage = '';
  tempDataLoaded: boolean = false;

  // Temp Chart
  temperatureDataRows: TemperatureDataRow[] = [];
  xyTempDataSeries: XyDataSeries = {} as XyDataSeries;
  xyMaxTempDataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverXDataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverX1DataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverX2DataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverX3DataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverX4DataSeries: XyDataSeries = {} as XyDataSeries;
  tempOverXDataSeriesCollection: XyDataSeries[] = [];

  tick: number = 0;
  tempData: ProjectTemperatureData = {} as ProjectTemperatureData;
  sciChartSurface?: SciChartSurface; // Store reference to chart surface
  isInitialLoad: boolean = true; // Track if this is the first load

  rolloverModifier?: RolloverModifier;
  zoomPanModifier?: ZoomPanModifier;
  rubberBandZoomModifier?: RubberBandXyZoomModifier;

  // channelrange_start: number = 0;
  // channelrange_end: number = 4;
  // channelrange_span: number = 4;
  // samplingInterval: number = 1;
  maxTempData: number[][] = [];
  tempAboveXData: number[][] = [];
  tempAboveX1Data: number[][] = [];
  tempAboveX2Data: number[][] = [];
  tempAboveX3Data: number[][] = [];
  tempAboveX4Data: number[][] = [];

  tempAboveXDataCollection: number[][][] = [];
  tempAbovePoint: number = 70;
  isRunning: boolean = false;

  rangeSliderLeftVal: number = 0;
  rangeSliderRightVal: number = 100;
  showRangeSlider: boolean = false;

  // Chart Controls Events
  public onZoomOut?: () => void;
  public onAddMarker?: () => void;
  public onTempAbovePointChanged?: (temp: number) => void;
  public onRangeSliderChange?: (left: number, right: number) => void;

  selectedProjectSubscription: Subscription = {} as Subscription;
  tempDataSubscription: Subscription = {} as Subscription;
  tempRTDataSubscription: Subscription = {} as Subscription;
  tickSubscription: Subscription = {} as Subscription;
  vert_annot_positionsSubscription: Subscription = {} as Subscription;

  ann_t2: HorizontalLineAnnotation = {} as HorizontalLineAnnotation;

  constructor(public projectService: ProjectService, private chartCapture: ChartCaptureService) {}

  ngOnInit() {
    //console.log('Temp OnInit called');
    this.initSciChart();
    this.setupProjectSubscription();
    this.createTempGraph();
    this.projectService.isHelpOverlayVisible = false;
  }

  ngOnDestroy(): void {
    // Dispose of the chart surface to free up resources
    if (this.sciChartSurface) {
      this.chartCapture.unregister('curingGraph', this.sciChartSurface);
      this.sciChartSurface.delete();
    }
    
    this.selectedProjectSubscription.unsubscribe();
    this.tempDataSubscription.unsubscribe();
    this.tempRTDataSubscription.unsubscribe();
    //this.tickSubscription.unsubscribe();
    this.vert_annot_positionsSubscription.unsubscribe();
  }

  // Setup subscription to recreate chart when project changes
  setupProjectSubscription() {
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (projectName) => {
          if (projectName && projectName.length > 0) {
            console.log('Curing > Project changed to:', projectName);
            console.log('Curing > Current unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
            
            // Skip recreation on initial load (graph is already being created in ngOnInit)
            if (this.isInitialLoad) {
              this.isInitialLoad = false;
              console.log('Curing > Skipping recreation on initial load');
              return;
            }
            
            // Recreate the graph with the new project's unit settings
            this.recreateTempGraph();
          }
        },
        (error) => (this.errorMessage = error)
      );
  }

  // Recreate the temperature graph (called when project changes)
  async recreateTempGraph() {
    console.log('Curing > Recreating graph with units:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
    
    // Dispose of the existing chart if it exists
    if (this.sciChartSurface) {
      console.log('Curing > Disposing existing chart');
      this.chartCapture.unregister('curingGraph', this.sciChartSurface);
      this.sciChartSurface.delete();
      this.sciChartSurface = undefined;
    }

    // Clear data
    this.maxTempData = [];
    this.tempAboveXData = [];
    this.tempAboveX1Data = [];
    this.tempAboveX2Data = [];
    this.tempAboveX3Data = [];
    this.tempAboveX4Data = [];
    this.tempAboveXDataCollection = [];

    // Recreate the chart
    await this.createTempGraph();
  }

  async initSciChart() {
    SciChartSurface.useWasmFromCDN();
    SciChartSurface.setRuntimeLicenseKey(environment.sciChartKey);
  }

  // Curing Graph ===========================================================================

  async createTempGraph() {
    console.log('Curing > Creating graph with units:', this.projectService.isMetricUnit ? 'Metric (m, °C)' : 'Imperial (ft, °F)');
    
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(
      'curing-graph'
    );

    // Store reference to the chart surface
    this.sciChartSurface = sciChartSurface;
    this.chartCapture.register('curingGraph', sciChartSurface, 'curing-graph');

    // Create an X,Y Axis and add to the chart
    const xAxis = new NumericAxis(wasmContext, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
    });

    const yAxis = new NumericAxis(wasmContext, {
      //autoRange: EAutoRange.Always,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'),
    });
    yAxis.axisAlignment = EAxisAlignment.Left;
    yAxis.growBy = new NumberRange(0.2, 0.2);

    const yAxisTimeDuration = new NumericAxis(wasmContext, {
      id: 'YAxis_TimeDuration',
      //autoRange: EAutoRange.Always,
      drawLabels: true,
      axisAlignment: EAxisAlignment.Right,
      growBy: new NumberRange(0, 0.2),
    });

    yAxisTimeDuration.labelProvider.formatLabel =
      this.projectService.formatUnixDateToHumanStringHHMMSS_Custom;

    sciChartSurface.xAxes.add(xAxis);
    sciChartSurface.yAxes.add(yAxis);
    sciChartSurface.yAxes.add(yAxisTimeDuration);

    // horizontal Line annotation
    this.ann_t2 = new HorizontalLineAnnotation({
      labelPlacement: ELabelPlacement.Axis,
      showLabel: true,
      stroke: '#24A948',
      strokeThickness: 3,
      y1: this.tempAbovePoint,
      axisLabelFill: '#24A948',
      axisFontSize: 14,
      isEditable: true,
      onDrag: (args) => {},
    });

    sciChartSurface.annotations.add(this.ann_t2);

    const customTemplate = (heatmapSeries3d: any, tooltipTitle: any) => {
      const valuesWithLabels = [];
      if (tooltipTitle) {
        valuesWithLabels.push(tooltipTitle);
      }
      const heatmapSeriesInfo = heatmapSeries3d;

      if (
        tooltipTitle == 'Current temperature' ||
        tooltipTitle == 'Maximal temperature'
      ) {
        valuesWithLabels.push(`${heatmapSeriesInfo.formattedYValue}`);
      } else {
        valuesWithLabels.push(
          `${this.projectService.formatUnixDateToHumanStringHHMMSS_Custom(
            Math.floor(heatmapSeriesInfo.formattedYValue)
          )}`
        );
        //valuesWithLabels.push(`${heatmapSeriesInfo.formattedYValue}`);
      }

      return valuesWithLabels;
    };
    this.rolloverModifier = new RolloverModifier({
      tooltipDataTemplate: customTemplate,
    });
    sciChartSurface.chartModifiers.add(this.rolloverModifier);

    // Create new modifier instances to avoid "already attached" errors
    this.rubberBandZoomModifier = new RubberBandXyZoomModifier();
    this.rubberBandZoomModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.rubberBandZoomModifier);

    this.zoomPanModifier = new ZoomPanModifier();
    this.zoomPanModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.zoomPanModifier);

    // Create temp line series
    this.xyTempDataSeries = new XyDataSeries(wasmContext);
    this.xyTempDataSeries.dataSeriesName = 'Current temperature';

    const TempLineSeries = new FastLineRenderableSeries(wasmContext, {
      stroke: '#ffffff',
      strokeThickness: 2,
      dataSeries: this.xyTempDataSeries,
    });
    TempLineSeries.rolloverModifierProps.tooltipColor = '#F3FEF8';
    TempLineSeries.rolloverModifierProps.tooltipTextColor = '#3B3D3C';
    // sciChartSurface.renderableSeries.add(TempLineSeries);

    // Create Max temp line series
    this.xyMaxTempDataSeries = new XyDataSeries(wasmContext);
    this.xyMaxTempDataSeries.dataSeriesName = 'Maximal temperature';

    const maxTempLineSeries = new FastLineRenderableSeries(wasmContext, {
      stroke: '#F71C1C',
      strokeThickness: 2,
      dataSeries: this.xyMaxTempDataSeries,
    });
    maxTempLineSeries.rolloverModifierProps.tooltipColor = '#F71C1C';
    maxTempLineSeries.rolloverModifierProps.tooltipTextColor = '#FFEFEC';
    // sciChartSurface.renderableSeries.add(maxTempLineSeries);

    sciChartSurface.chartModifiers.add(
      new LegendModifier({
        showCheckboxes: true,
        orientation: ELegendOrientation.Horizontal,
      })
    );

    // Create Temp Over X Mountain Series
    this.tempOverXDataSeries = new XyDataSeries(wasmContext);
    this.tempOverXDataSeries.dataSeriesName =
      'Time Over ' +
      this.tempAbovePoint +
      ' ' +
      (this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'));

    const mountainSeries = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverXDataSeries,
      stroke: '#30B453',
      strokeThickness: 1,
      zeroLineY: 0.0,
      opacity: 0.8,
      // when a solid color is required, use fill
      fill: 'rgba(48, 180, 83, 0.9)',
    });

    // sciChartSurface.renderableSeries.add(mountainSeries);

    // Mountain Series for Heat Inputs -----------------------------------------------
    // Create Temp Over X1 Mountain Series
    this.tempOverX1DataSeries = new XyDataSeries(wasmContext);
    this.tempOverX1DataSeries.dataSeriesName =
      'Time Over ' +
      this.tempAbovePoint +
      ' ' +
      (this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'));

    const mountainSeries1 = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverX1DataSeries,
      stroke: '#871EDF',
      strokeThickness: 1,
      zeroLineY: 0.0,
      opacity: 0.8,
      // when a solid color is required, use fill
      fill: 'rgba(135, 30, 223, 0.9)',
    });

    // Create Temp Over X2 Mountain Series
    this.tempOverX2DataSeries = new XyDataSeries(wasmContext);
    this.tempOverX2DataSeries.dataSeriesName =
      'Time Over ' +
      this.tempAbovePoint +
      ' ' +
      (this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'));

    const mountainSeries2 = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverX2DataSeries,
      stroke: '#9EDC37',
      strokeThickness: 1,
      zeroLineY: 0.0,
      opacity: 0.8,
      // when a solid color is required, use fill
      fill: 'rgba(158, 220, 55, 0.9)',
    });

    // Create Temp Over X3 Mountain Series
    this.tempOverX3DataSeries = new XyDataSeries(wasmContext);
    this.tempOverX3DataSeries.dataSeriesName =
      'Time Over ' +
      this.tempAbovePoint +
      ' ' +
      (this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'));

    const mountainSeries3 = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverX3DataSeries,
      stroke: '#F56442',
      strokeThickness: 1,
      zeroLineY: 0.0,
      opacity: 0.8,
      // when a solid color is required, use fill
      fill: 'rgba(245, 100, 66, 0.9)',
    });

    // Create Temp Over X3 Mountain Series
    this.tempOverX4DataSeries = new XyDataSeries(wasmContext);
    this.tempOverX4DataSeries.dataSeriesName =
      'Time Over ' +
      this.tempAbovePoint +
      ' ' +
      (this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'));

    const mountainSeries4 = new FastMountainRenderableSeries(wasmContext, {
      yAxisId: 'YAxis_TimeDuration',
      dataSeries: this.tempOverX4DataSeries,
      stroke: '#328BE3',
      strokeThickness: 1,
      zeroLineY: 0.0,
      opacity: 0.8,
      // when a solid color is required, use fill
      fill: 'rgba(50, 139, 227, 0.9)',
    });

    // Data Subscriptions --------------------------------------------------------
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (data) => {
          if (this.xyTempDataSeries && this.xyTempDataSeries.count() > 0) {
            this.xyTempDataSeries.clear();
          }
        },
        (error) => (this.errorMessage = error)
      );

    // Unsubscribe from previous subscriptions if they exist to prevent memory leaks
    if (this.tempDataSubscription && typeof this.tempDataSubscription.unsubscribe === 'function') {
      this.tempDataSubscription.unsubscribe();
    }
    this.tempDataSubscription = this.projectService.temperatureData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Curing Comp: Temp data is null.');
          return;
        } else if (data.TRC == null) {
          console.log('Error in Curing Comp: Temp data > TRC is null.');
          return;
        }

        this.tempData = data;
        this.temperatureDataRows = data.TRC;
        this.tempAbovePoint = data.CT;
        this.ann_t2.y1 = this.tempAbovePoint;
        this.tempOverXDataSeries.dataSeriesName =
          'Time Over ' +
          this.tempAbovePoint +
          ' ' +
          (this.projectService.isMetricUnit
            ? String.fromCharCode(176).concat('C')
            : String.fromCharCode(176).concat('F'));

        this.updateCuringData(
          sciChartSurface,
          TempLineSeries,
          maxTempLineSeries,
          mountainSeries,
          mountainSeries1,
          mountainSeries2,
          mountainSeries3,
          mountainSeries4
        );

        this.updateTempChartForTick('0');
        //sciChartSurface.zoomExtents();
      },
      (error) => (this.errorMessage = error)
    );

    this.tempRTDataSubscription =
      this.projectService.temperatureRTData.subscribe(
        (data) => {
          console.log('Curing comp > temperatureRTData.subscribe');
          if (data == null) {
            console.log('Error in Curing Comp: Temp RT data is null.');
            return;
          } else if (data.TRC == null) {
            console.log('Error in Curing Comp: Temp RT data > TRC is null.');
            return;
          }

          if (data.TRC.length > 0) {
            this.temperatureDataRows.push(data.TRC[0]);

            console.log(
              'Curing comp > pushed next TimeStamp Data. Count: ' +
                this.temperatureDataRows.length
            );

            this.updateCuringData(
              sciChartSurface,
              TempLineSeries,
              maxTempLineSeries,
              mountainSeries,
              mountainSeries1,
              mountainSeries2,
              mountainSeries3,
              mountainSeries4
            );

            var lastPos = this.temperatureDataRows.length - 1;
            this.updateTempChartForTick(lastPos.toString());

            //sciChartSurface.zoomExtents();
          }
        },
        (error) => (this.errorMessage = error)
      );

    // this.tickSubscription = this.projectService.tickPosition.subscribe(
    //   (data) => {
    //     this.updateTempChartForTick(data.toString());

    //     sciChartSurface.zoomExtents();
    //     const linerLength = this.tempData.CRE - this.tempData.CRB;
    //     const visibleLeft = Math.round(
    //       (this.rangeSliderLeftVal / 100) * linerLength
    //     );
    //     const visibleRight = Math.round(
    //       (this.rangeSliderLeftVal / 100) * linerLength
    //     );

    //     this.adjustYAxisVisibleRange(yAxis);
    //   },
    //   (error) => (this.errorMessage = error)
    // );

    // Listen to Control Events:
    this.onZoomOut = () => {
      console.log(this.tempData.CRB + '-' + this.tempData.CRE);
      //sciChartSurface.zoomExtents();

      sciChartSurface.zoomExtents();
      if (this.showRangeSlider) {
        this.toggleRangeSlider();
      }

      this.adjustYAxisVisibleRange(yAxis);
    };

    this.onRangeSliderChange = (left, right) => {
      //console.log(left + '-' + right);
      const linerLength = this.tempData.CRE - this.tempData.CRB;
      const visibleLeft = Math.round((left / 100) * linerLength);
      const visibleRight = Math.round((right / 100) * linerLength);
      xAxis.visibleRange = new NumberRange(visibleLeft, visibleRight);
    };

    this.vert_annot_positionsSubscription =
      this.projectService.vert_annot_positions.subscribe(
        (data) => {
          this.updateVerticalAnnotations(sciChartSurface, data);
        },
        (error) => (this.errorMessage = error)
      );

    this.onAddMarker = () => {
      this.projectService.addMarker();
    };
  }

  formatxAxisTemperatureGraph(dataValue: number) {
    //adjust the value that user sees on the axis from 0 to end of range instead of from startRange to endRange.
    return (dataValue - this.tempData.CRB).toFixed(2) +
      this.projectService.isMetricUnit
      ? 'm'
      : 'ft';
  }

  updateCuringData(
    sciChart: SciChartSurface,
    tempLineSeries: FastLineRenderableSeries,
    maxTempLineSeries: FastLineRenderableSeries,
    mountainSeries: FastMountainRenderableSeries,
    mountainSeries1: FastMountainRenderableSeries,
    mountainSeries2: FastMountainRenderableSeries,
    mountainSeries3: FastMountainRenderableSeries,
    mountainSeries4: FastMountainRenderableSeries
  ) {
    // Compute Max Temperature Series data(temp array) for each Ticks

    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    console.log('HM-' + this.tempData.HMC);

    const maxTemperatureData = zeroArray2D([
      this.temperatureDataRows.length,
      endDataPoint - startDataPoint,
    ]);

    this.tempAboveXDataCollection = [];
    this.tempOverXDataSeriesCollection = [];

    // Add temp over x for Curing Temp
    const temperatureAboveXData_Curing = zeroArray2D([
      this.temperatureDataRows.length,
      endDataPoint - startDataPoint,
    ]);

    this.tempAboveX1Data = zeroArray2D([
      this.temperatureDataRows.length,
      endDataPoint - startDataPoint,
    ]);

    this.tempAboveX2Data = zeroArray2D([
      this.temperatureDataRows.length,
      endDataPoint - startDataPoint,
    ]);

    this.tempAboveX3Data = zeroArray2D([
      this.temperatureDataRows.length,
      endDataPoint - startDataPoint,
    ]);

    this.tempAboveX4Data = zeroArray2D([
      this.temperatureDataRows.length,
      endDataPoint - startDataPoint,
    ]);

    // Set Temp Above X1

    const x1Point =
      this.tempData.HMC.length > 0 ? this.tempData.HMC[0].HT : 1000;

    const x1Min = this.tempData.HMC.length > 0 ? this.tempData.HMC[0].HM : 0;

    if (x1Point < 1000) {
      this.tempOverX1DataSeries.dataSeriesName =
        'Time Over ' +
        x1Point.toFixed(1) +
        ' ' +
        (this.projectService.isMetricUnit
          ? String.fromCharCode(176).concat('C')
          : String.fromCharCode(176).concat('F')) +
        ' | ' +
        x1Min +
        ' min';

      if (!sciChart.renderableSeries.contains(mountainSeries1)) {
        sciChart.renderableSeries.add(mountainSeries1);
      }
    } else {
      if (sciChart.renderableSeries.contains(mountainSeries1)) {
        sciChart.renderableSeries.remove(mountainSeries1);
      }
    }

    const x2Point =
      this.tempData.HMC.length > 1 ? this.tempData.HMC[1].HT : 1000;

    const x2Min = this.tempData.HMC.length > 1 ? this.tempData.HMC[1].HM : 0;

    if (x2Point < 1000) {
      this.tempOverX2DataSeries.dataSeriesName =
        'Time Over ' +
        x2Point.toFixed(1) +
        ' ' +
        (this.projectService.isMetricUnit
          ? String.fromCharCode(176).concat('C')
          : String.fromCharCode(176).concat('F')) +
        ' | ' +
        x2Min +
        ' min';

      //console.log('x2: ' + x2Point);
      if (!sciChart.renderableSeries.contains(mountainSeries2)) {
        sciChart.renderableSeries.add(mountainSeries2);
      }
    } else {
      if (sciChart.renderableSeries.contains(mountainSeries2)) {
        sciChart.renderableSeries.remove(mountainSeries2);
      }
    }

    const x3Point =
      this.tempData.HMC.length > 2 ? this.tempData.HMC[2].HT : 1000;

    const x3Min = this.tempData.HMC.length > 2 ? this.tempData.HMC[2].HM : 0;

    if (x3Point < 1000) {
      this.tempOverX3DataSeries.dataSeriesName =
        'Time Over ' +
        x3Point.toFixed(1) +
        ' ' +
        (this.projectService.isMetricUnit
          ? String.fromCharCode(176).concat('C')
          : String.fromCharCode(176).concat('F')) +
        ' | ' +
        x3Min +
        ' min';

      if (!sciChart.renderableSeries.contains(mountainSeries3)) {
        sciChart.renderableSeries.add(mountainSeries3);
      }
    } else {
      if (sciChart.renderableSeries.contains(mountainSeries3)) {
        sciChart.renderableSeries.remove(mountainSeries3);
      }
    }

    const x4Point =
      this.tempData.HMC.length > 3 ? this.tempData.HMC[3].HT : 1000;

    const x4Min = this.tempData.HMC.length > 3 ? this.tempData.HMC[3].HM : 0;

    if (x4Point < 1000) {
      this.tempOverX4DataSeries.dataSeriesName =
        'Time Over ' +
        x4Point.toFixed(1) +
        ' ' +
        (this.projectService.isMetricUnit
          ? String.fromCharCode(176).concat('C')
          : String.fromCharCode(176).concat('F')) +
        ' | ' +
        x4Min +
        ' min';

      if (!sciChart.renderableSeries.contains(mountainSeries4)) {
        sciChart.renderableSeries.add(mountainSeries4);
      }
    } else {
      if (sciChart.renderableSeries.contains(mountainSeries4)) {
        sciChart.renderableSeries.remove(mountainSeries4);
      }
    }

    // Add Temp Mountain Series, Cur Temp and Max Temp Later (to make it appear after Heat Input series)
    if (sciChart.renderableSeries.contains(mountainSeries)) {
      sciChart.renderableSeries.remove(mountainSeries);
    }
    sciChart.renderableSeries.add(mountainSeries);

    if (sciChart.renderableSeries.contains(tempLineSeries)) {
      sciChart.renderableSeries.remove(tempLineSeries);
    }
    sciChart.renderableSeries.add(tempLineSeries);

    if (sciChart.renderableSeries.contains(maxTempLineSeries)) {
      sciChart.renderableSeries.remove(maxTempLineSeries);
    }
    sciChart.renderableSeries.add(maxTempLineSeries);

    for (let i = 0; i < this.temperatureDataRows.length; i++) {
      for (let j = startDataPoint; j < endDataPoint; j++) {
        // Set Max Temp
        if (i == 0) {
          maxTemperatureData[i][j - startDataPoint] =
            this.temperatureDataRows[i].TR[j];
        } else {
          maxTemperatureData[i][j - startDataPoint] = Math.max(
            this.temperatureDataRows[i].TR[j],
            maxTemperatureData[i - 1][j - startDataPoint]
          );
        }

        // Set Temp Above X

        if (i == 0) {
          temperatureAboveXData_Curing[i][j - startDataPoint] = 0;
        } else if (this.temperatureDataRows[i].TR[j] < this.tempAbovePoint) {
          temperatureAboveXData_Curing[i][j - startDataPoint] =
            temperatureAboveXData_Curing[i - 1][j - startDataPoint];
        } else {
          // Find Time Diff between current and Prev Tick and Add to Prev Time Above Duration
          const unixTimeInterval =
            (this.temperatureDataRows[i].UT -
              this.temperatureDataRows[i - 1].UT) /
            1;

          temperatureAboveXData_Curing[i][j - startDataPoint] =
            temperatureAboveXData_Curing[i - 1][j - startDataPoint] +
            unixTimeInterval;
        }

        // Set Temp Over X1

        if (x1Point < 1000) {
          if (i == 0) {
            this.tempAboveX1Data[i][j - startDataPoint] = 0;
          } else if (this.temperatureDataRows[i].TR[j] < x1Point) {
            this.tempAboveX1Data[i][j - startDataPoint] =
              this.tempAboveX1Data[i - 1][j - startDataPoint];
          } else {
            // Find Time Diff between current and Prev Tick and Add to Prev Time Above Duration
            const unixTimeInterval =
              (this.temperatureDataRows[i].UT -
                this.temperatureDataRows[i - 1].UT) /
              1;

            this.tempAboveX1Data[i][j - startDataPoint] =
              this.tempAboveX1Data[i - 1][j - startDataPoint] +
              unixTimeInterval;
          }
        }

        // Set Temp Over X2

        if (x2Point < 1000) {
          if (i == 0) {
            this.tempAboveX2Data[i][j - startDataPoint] = 0;
          } else if (this.temperatureDataRows[i].TR[j] < x2Point) {
            this.tempAboveX2Data[i][j - startDataPoint] =
              this.tempAboveX2Data[i - 1][j - startDataPoint];
          } else {
            // Find Time Diff between current and Prev Tick and Add to Prev Time Above Duration
            const unixTimeInterval =
              (this.temperatureDataRows[i].UT -
                this.temperatureDataRows[i - 1].UT) /
              1;

            this.tempAboveX2Data[i][j - startDataPoint] =
              this.tempAboveX2Data[i - 1][j - startDataPoint] +
              unixTimeInterval;
          }
        }

        // Set Temp Over X3

        if (x3Point < 1000) {
          if (i == 0) {
            this.tempAboveX3Data[i][j - startDataPoint] = 0;
          } else if (this.temperatureDataRows[i].TR[j] < x3Point) {
            this.tempAboveX3Data[i][j - startDataPoint] =
              this.tempAboveX3Data[i - 1][j - startDataPoint];
          } else {
            // Find Time Diff between current and Prev Tick and Add to Prev Time Above Duration
            const unixTimeInterval =
              (this.temperatureDataRows[i].UT -
                this.temperatureDataRows[i - 1].UT) /
              1;

            this.tempAboveX3Data[i][j - startDataPoint] =
              this.tempAboveX3Data[i - 1][j - startDataPoint] +
              unixTimeInterval;
          }
        }

        // Set Temp Over X4

        if (x4Point < 1000) {
          if (i == 0) {
            this.tempAboveX4Data[i][j - startDataPoint] = 0;
          } else if (this.temperatureDataRows[i].TR[j] < x4Point) {
            this.tempAboveX4Data[i][j - startDataPoint] =
              this.tempAboveX4Data[i - 1][j - startDataPoint];
          } else {
            // Find Time Diff between current and Prev Tick and Add to Prev Time Above Duration
            const unixTimeInterval =
              (this.temperatureDataRows[i].UT -
                this.temperatureDataRows[i - 1].UT) /
              1;

            this.tempAboveX4Data[i][j - startDataPoint] =
              this.tempAboveX4Data[i - 1][j - startDataPoint] +
              unixTimeInterval;
          }
        }
      }
    }

    this.maxTempData = maxTemperatureData;
    this.tempAboveXData = temperatureAboveXData_Curing;
  }

  updateTempChartForTick(val: string) {
    const sliderVal: number = +val;
    this.tick = sliderVal;

    // Update Temp Graph
    if (this.tempDataLoaded) {
      this.xyTempDataSeries.clear();
      this.xyMaxTempDataSeries.clear();
      this.tempOverXDataSeries.clear();
      this.tempOverX1DataSeries.clear();
      this.tempOverX2DataSeries.clear();
      this.tempOverX3DataSeries.clear();
      this.tempOverX4DataSeries.clear();
      //console.log('curing data cleared');
    }

    if (this.temperatureDataRows.length == 0) {
      this.tempDataLoaded = false;
      return;
    } else {
      this.tempDataLoaded = true;
    }

    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    var xValues_Temp: number[] = new Array(endDataPoint - startDataPoint);
    var yValues_Temp: number[] = new Array(endDataPoint - startDataPoint);

    var xValues_MaxTemp: number[] = new Array(
      this.maxTempData[sliderVal].length
    );
    var yValues_MaxTemp: number[] = new Array(
      this.maxTempData[sliderVal].length
    );

    var xValues_OverX: number[] = new Array(
      this.tempAboveXData[sliderVal].length
    );
    var yValues_OverX: number[] = new Array(
      this.tempAboveXData[sliderVal].length
    );

    var xValues_OverX1: number[] = new Array(
      this.tempAboveX1Data[sliderVal].length
    );
    var yValues_OverX1: number[] = new Array(
      this.tempAboveX1Data[sliderVal].length
    );

    var xValues_OverX2: number[] = new Array(
      this.tempAboveX2Data[sliderVal].length
    );
    var yValues_OverX2: number[] = new Array(
      this.tempAboveX2Data[sliderVal].length
    );

    var xValues_OverX3: number[] = new Array(
      this.tempAboveX3Data[sliderVal].length
    );
    var yValues_OverX3: number[] = new Array(
      this.tempAboveX3Data[sliderVal].length
    );

    var xValues_OverX4: number[] = new Array(
      this.tempAboveX4Data[sliderVal].length
    );
    var yValues_OverX4: number[] = new Array(
      this.tempAboveX4Data[sliderVal].length
    );

    // Create empty collection of temp over X Array for each Heat Marker
    var yValues_OverXCollection: number[][] = [];

    // for (
    //   let heatMarkerIndex = 0;
    //   heatMarkerIndex < this.tempData.HMC.length;
    //   heatMarkerIndex++
    // ) {
    //   const hmTemp = this.tempData.HMC[heatMarkerIndex];

    //   var yValues_OverX: number[] = new Array(
    //     this.tempAboveXDataCollection[0][sliderVal].length
    //   );

    //   yValues_OverXCollection.push(yValues_OverX);
    // }

    // X Factor = Liner Length / Data Points Count

    const xFactor =
      (this.tempData.CRE - this.tempData.CRB) / (endDataPoint - startDataPoint);
    //console.log(xFactor);
    //console.log('dp start:' + startDataPoint + '  end:' + endDataPoint);

    for (let j = startDataPoint; j < endDataPoint; j++) {
      try {
        //xValues_Temp[j] = j * xFactor;
        xValues_Temp[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_Temp[j - startDataPoint] =
          this.temperatureDataRows[sliderVal].TR[j] ?? 0;
      } catch (error) {
        console.log('error in xyTempDataSeries.append: ' + error + ' - ' + j);
      }

      try {
        xValues_MaxTemp[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_MaxTemp[j - startDataPoint] =
          this.maxTempData[sliderVal][j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in xyMaxTempDataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX[j - startDataPoint] =
          this.tempAboveXData[sliderVal][j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverXDataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX1[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX1[j - startDataPoint] =
          this.tempAboveX1Data[sliderVal][j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverX1DataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX2[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX2[j - startDataPoint] =
          this.tempAboveX2Data[sliderVal][j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverX2DataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX3[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX3[j - startDataPoint] =
          this.tempAboveX3Data[sliderVal][j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverX3DataSeries.append: ' + error + ' - ' + j
        );
      }

      try {
        xValues_OverX4[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues_OverX4[j - startDataPoint] =
          this.tempAboveX4Data[sliderVal][j - startDataPoint] ?? 0;
      } catch (error) {
        console.log(
          'error in tempOverX4DataSeries.append: ' + error + ' - ' + j
        );
      }

      // try {
      //   for (
      //     let heatMarkerIndex = 0;
      //     heatMarkerIndex < this.tempData.HMC.length;
      //     heatMarkerIndex++
      //   ) {
      //     const myValues_OverX = yValues_OverXCollection[heatMarkerIndex];
      //     myValues_OverX[j - startDataPoint] =
      //       this.tempAboveXDataCollection[heatMarkerIndex][sliderVal][
      //         j - startDataPoint
      //       ] ?? 0;
      //   }
      // } catch (error) {
      //   console.log(
      //     'error in tempOverXDataSeries.append: ' + error + ' - ' + j
      //   );
      // }
    }

    this.xyTempDataSeries.appendRange(xValues_Temp, yValues_Temp);
    this.xyMaxTempDataSeries.appendRange(xValues_MaxTemp, yValues_MaxTemp);
    this.tempOverXDataSeries.appendRange(xValues_OverX, yValues_OverX);
    this.tempOverX1DataSeries.appendRange(xValues_OverX1, yValues_OverX1);
    this.tempOverX2DataSeries.appendRange(xValues_OverX2, yValues_OverX2);
    this.tempOverX3DataSeries.appendRange(xValues_OverX3, yValues_OverX3);
    this.tempOverX4DataSeries.appendRange(xValues_OverX4, yValues_OverX4);

    // for (
    //   let heatMarkerIndex = 0;
    //   heatMarkerIndex < this.tempData.HMC.length;
    //   heatMarkerIndex++
    // ) {
    //   const myValues_OverX = yValues_OverXCollection[heatMarkerIndex];
    //   this.tempOverXDataSeriesCollection[heatMarkerIndex].appendRange(
    //     xValues_OverX,
    //     myValues_OverX
    //   );
    // }
  }

  setRollOver(val: boolean) {
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = val;
  }
  setPan(val: boolean) {
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = val;
  }
  setZoom(val: boolean) {
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = val;
  }

  zoomOut() {
    if (!this.onZoomOut) return;
    this.onZoomOut();
  }

  addMarker() {
    if (!this.onAddMarker) return;
    this.onAddMarker();
  }

  convertRgbStringToColor(rgbString: string): string {
    // Convert "r, g, b" string to hex format for SciChart
    if (!rgbString) {
      return '#7F30F3'; // Default color
    }
    
    // If it's already in hex format, return as is
    if (rgbString.startsWith('#')) {
      return rgbString;
    }
    
    // Parse "r, g, b" string and convert to hex
    const parts = rgbString.split(',').map(s => s.trim());
    if (parts.length !== 3) {
      return '#7F30F3'; // Default color if format is invalid
    }
    
    const r = parseInt(parts[0]);
    const g = parseInt(parts[1]);
    const b = parseInt(parts[2]);
    
    // Convert to hex format
    const toHex = (n: number) => {
      const hex = n.toString(16).padStart(2, '0');
      return hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  updateVerticalAnnotations(sciChart: SciChartSurface, markers: MarkerInfo[]) {
    console.log('CuringComponent > updateVerticalAnnotations called with markers:', markers);
    
    // Clear all annotations except the ann_t2 annotation
    const annotationsToRemove: any[] = [];
    sciChart.annotations.asArray().forEach(ann => {
      if (ann !== this.ann_t2) {
        annotationsToRemove.push(ann);
      }
    });
    annotationsToRemove.forEach(ann => sciChart.annotations.remove(ann));
    console.log('CuringComponent > Marker annotations cleared, keeping ann_t2');

    if (!markers || markers.length === 0) {
      console.log('CuringComponent > No markers to add');
      return;
    }

    markers.forEach((mar, index) => {
      // Convert RGB string "r, g, b" to format SciChart understands
      const colorString = this.convertRgbStringToColor(mar.C);
      
      const annt = new VerticalLineAnnotation({
        labelPlacement: ELabelPlacement.Axis,
        showLabel: true,
        stroke: colorString,
        strokeThickness: 3,
        x1: mar.P,
        axisLabelFill: colorString,
        axisFontSize: 14,
        isEditable: true,
        isSelected: false,
        onDragStarted: () => {
          // Increase thickness during drag for better visibility
          annt.strokeThickness = 6;
        },
        onDragEnded: () => {
          // Update the position in the internal array
          mar.P = +annt.x1.toFixed(2);
          // Persist to API
          this.projectService.updateVerticalAnnotationPosition(
            index,
            +annt.x1.toFixed(2)
          );
          // Reset to normal thickness after drag
          annt.strokeThickness = 3;
        },
      });

      sciChart.annotations.add(annt);
    });
  }

  toggleRangeSlider() {
    this.showRangeSlider = !this.showRangeSlider;

    if (!this.onRangeSliderChange) return;
    this.onRangeSliderChange(0, 100);
  }

  updateChartVisibleRange(leftVal: string, rightVal: string) {
    if (!this.onRangeSliderChange) return;

    if (+leftVal <= +rightVal) {
      this.rangeSliderLeftVal = +leftVal;
      this.rangeSliderRightVal = +rightVal;
    } else {
      this.rangeSliderLeftVal = +rightVal;
      this.rangeSliderRightVal = +leftVal;
    }

    this.onRangeSliderChange(this.rangeSliderLeftVal, this.rangeSliderRightVal);
  }

  toggleHelpOverlay() {
    this.projectService.isHelpOverlayVisible =
      !this.projectService.isHelpOverlayVisible;
  }

  adjustYAxisVisibleRange(yAxis_Temp: NumericAxis) {
    var visibleMin = 0;
    if (yAxis_Temp.visibleRange.min < -10) {
      visibleMin = -10;
    }
    if (yAxis_Temp.visibleRange.max < this.tempAbovePoint + 10) {
      yAxis_Temp.visibleRange = new NumberRange(
        visibleMin,
        this.tempAbovePoint + 10
      );
    } else if (yAxis_Temp.visibleRange.min < -10) {
      yAxis_Temp.visibleRange = new NumberRange(
        visibleMin,
        yAxis_Temp.visibleRange.max
      );
    }
  }
}
